import React, { useState } from 'react';
import {
  Container,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useInstitution } from '../../context/InstitutionContext';

export default function AddBranch() {
  const navigate = useNavigate();
  const { currentInstitution, createBranch, loading } = useInstitution();
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    duration_years: 4,
    intake_capacity: 100,
    course_type: 'UG',
    hod_id: ''
  });

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'duration_years' || name === 'intake_capacity' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      if (!formData.name.trim()) {
        setError('Branch name is required');
        return;
      }
      if (!formData.code.trim()) {
        setError('Branch code is required');
        return;
      }

      if (!currentInstitution?.id) {
        setError('Institution not found');
        return;
      }

      await createBranch(currentInstitution.id, formData);
      
      setFormData({
        name: '',
        code: '',
        description: '',
        duration_years: 4,
        intake_capacity: 100,
        course_type: 'UG',
        hod_id: ''
      });

      navigate('/admin-dashboard');
    } catch (err) {
      console.error('Error creating branch:', err);
      setError(err.message || 'Failed to create branch');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Add New Branch
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Create a new branch for {currentInstitution?.name}
        </Typography>
      </Box>

      <Card>
        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Branch Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Computer Science"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Branch Code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="e.g., CS"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Branch description"
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="text"
                  label="Duration (Years)"
                  name="duration_years"
                  value={formData.duration_years}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="text"
                  label="Intake Capacity"
                  name="intake_capacity"
                  value={formData.intake_capacity}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Course Type"
                  name="course_type"
                  value={formData.course_type}
                  onChange={handleChange}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="UG">Undergraduate</option>
                  <option value="PG">Postgraduate</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="HOD ID (Optional)"
                  name="hod_id"
                  value={formData.hod_id}
                  onChange={handleChange}
                  placeholder="Head of Department"
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin-dashboard')}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={24} /> : 'Create Branch'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}
